import React, { forwardRef } from 'react'
import { Pressable, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import type { FigmaGlyph } from '../../icons/glyph'
import { Icon } from '../../icons/Icon'
import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { checkboxContract } from './checkbox.contract'

const STATUSES = checkboxContract.props.status.values

export type CheckboxStatus = (typeof STATUSES)[number]

export const CHECKBOX_LABEL_VARIANT = variantForStyleId(checkboxContract.textStyleIds[0])

type ContractCell = {
    container: string
    disabled: string
    icon: string
    parts: Record<string, string>
    glyphs: Record<string, FigmaGlyph>
}

const cellFor = (status: CheckboxStatus): ContractCell => {
    const key = `status=${status}`
    const found = (checkboxContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds Checkbox: the Figma contract has no "${key}"`)
    }
    return found
}

/**
 * Figma names the box differently depending on what is in it — `Rectangle` when
 * empty, `Rectangle 20` when it holds a mark — and `parts` is keyed on the name
 * the file gives, so the component has to say which is which. Read rather than
 * invented: these are the keys the contract actually carries.
 */
const BOX = { unselected: 'Rectangle', selected: 'Rectangle 20', indeterminate: 'Rectangle 20' }
const BAR = 'Rectangle 28'
const TICK = 'Vector'

export type CheckboxProps = Omit<React.ComponentProps<typeof Pressable>, 'style' | 'children'> & {
    /** Figma's `showLabel` and `labelText` in one: the label is drawn when there is one. */
    children?: string
    status?: CheckboxStatus
    className?: string
}

type CheckboxRef = React.ComponentRef<typeof Pressable>

/**
 * A 20px box, a mark, and a label beside them.
 *
 * The whole visual is delegated: Figma draws the box as an instance of
 * `CheckboxUnselected` / `Selected` / `Indeterminate`, standalone components
 * with no states of their own, and applies the hover colour as an override
 * *inside* that instance. A dumper that stops at an instance reported all nine
 * variants as identical and empty.
 *
 * The tick is a `VECTOR` part, so the contract carries its path — `parts` gives
 * a part's ink and size, and for a long time that was all: the checkmark was
 * known to be 13.1x9.8 and had nothing to draw it with. The indeterminate bar
 * needs no such thing; it is a 12x2 rectangle, which is a View.
 */
export const Checkbox = forwardRef<CheckboxRef, CheckboxProps>(
    ({ children, status = 'unselected', disabled = false, className, ...rest }, ref) => {
        const cell = cellFor(status)
        return (
            <Pressable
                ref={ref}
                disabled={disabled}
                role='checkbox'
                accessibilityState={{
                    checked: status === 'indeterminate' ? 'mixed' : status === 'selected',
                    disabled: disabled ?? false,
                }}
                className={twMerge(
                    'flex-row',
                    cell.container,
                    disabled ? cell.disabled : undefined,
                    className,
                )}
                testID={`checkbox-${status}`}
                {...rest}
            >
                <View
                    className={twMerge('items-center justify-center', cell.parts[BOX[status]])}
                    testID='checkbox-box'
                >
                    {cell.glyphs[TICK] ? (
                        <Icon
                            glyph={cell.glyphs[TICK]}
                            // The tick's ink is the cell's, not the box's.
                            // Read from `parts` alone it rendered in whatever
                            // colour it inherited — `currentColor` against a
                            // filled box, which is the one place it must not be.
                            className={twMerge(cell.icon, cell.parts[TICK])}
                            testID='checkbox-tick'
                        />
                    ) : null}
                    {cell.parts[BAR] ? (
                        <View className={cell.parts[BAR]} testID='checkbox-bar' />
                    ) : null}
                </View>
                {children ? (
                    <Typography variant={CHECKBOX_LABEL_VARIANT} className={cell.parts.label}>
                        {children}
                    </Typography>
                ) : null}
            </Pressable>
        )
    },
)

Checkbox.displayName = 'Checkbox'

export { STATUSES as CHECKBOX_STATUSES }
